import type { ValidationIssue } from '@field-support/shared';

/** Thrown for every failed API call. `code` is the server's structured error code. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Validation problems from `details.issues` (VALIDATION_ERROR), or []. */
  get issues(): ValidationIssue[] {
    const issues = (this.details as { issues?: ValidationIssue[] } | undefined)?.issues;
    return Array.isArray(issues) ? issues : [];
  }

  /** The server's structured sub-cause (e.g. INVALID_CREDENTIALS), when it sent one. */
  get reason(): string | null {
    const reason = (this.details as { reason?: unknown } | undefined)?.reason;
    return typeof reason === 'string' ? reason : null;
  }
}

/**
 * The technical side of a failure goes to the console, never to the screen: the
 * interface shows only translated text chosen by error code (useErrorMessage).
 */
export function report(error: ApiError, cause?: unknown): ApiError {
  // eslint-disable-next-line no-console -- the one place technical error detail is meant to go.
  console.warn(`[api] ${error.code} (${error.status || 'no response'}): ${error.message}`, cause ?? error.details ?? '');
  return error;
}
